import { cn } from '@utazzo/ui';
import { Activity, ChevronRight, CornerDownRight, Percent, Users } from 'lucide-react';
import React, { useState } from 'react';

import {
  GRID_TABLE,
  labelCell,
  NUMBER_TONE,
  MONTH_ROW,
  MONTH_TABLE,
  MonthHeaderRow,
  REPORT_HEAD_CELL,
  TOTAL_LABEL_CELL,
  TOTAL_VALUE_CELL,
  useGroupOpen,
  valueCell,
} from './month-table';

import type {
  MarketingLeadStatusResponse,
  MarketingProfitMonth,
  MarketingStatusMonth,
} from '@utazzo/shared';

import { KpiCard, KpiGrid } from '@/features/reports/components/KpiCard';
import { ReportSection } from '@/features/reports/components/ReportSection';

const count = (value: number): string => value.toLocaleString('en-IN');
const pct = (value: number | null): string => (value === null ? '-' : `${value.toFixed(2)}%`);
/** Raw count with its percentage in brackets, e.g. "14 (19.72%)". */
const countPct = (countValue: number, pctValue: number | null): string =>
  pctValue === null ? count(countValue) : `${count(countValue)} (${pctValue.toFixed(2)}%)`;

/** QueryStatus code behind "Total Query Active This Month". */
const ACTIVE_CODE = 'active';

/**
 * "Query to Active Ratio" for the newest month in range: how much of what
 * marketing brought in that month is sitting in the Active stage right now.
 *
 * This is a CURRENT-STANDING measure - it counts leads whose status is Active
 * today. It is deliberately different from the "Query to Active Ratio" on the
 * Overview tab, which counts leads that EVER reached Active (from stage
 * history) and so runs higher, since leads that have since been won or lost
 * still count there but not here.
 */
const ActiveRatioSummary = ({
  status,
  loading,
}: {
  status: MarketingLeadStatusResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const monthLabel = status?.months[0]?.label;
  const totalQuery = status?.totals[0]?.count ?? 0;
  const active =
    status?.statuses.find((entry) => entry.code === ACTIVE_CODE)?.months[0]?.count ?? 0;
  const ratio = totalQuery > 0 ? (active / totalQuery) * 100 : null;
  const shimmer = (
    <span className="bg-muted inline-block h-6 w-14 animate-pulse rounded align-middle" />
  );
  const hint = monthLabel ? `Current month · ${monthLabel}` : undefined;

  return (
    <KpiGrid>
      <KpiCard
        icon={Users}
        tone="primary"
        value={loading || !status ? shimmer : count(totalQuery)}
        label="Total Query This Month"
        hint={hint}
      />
      <KpiCard
        icon={Activity}
        tone="success"
        value={loading || !status ? shimmer : count(active)}
        label="Total Query Active This Month"
        hint={hint}
      />
      <KpiCard
        icon={Percent}
        value={loading || !status ? shimmer : pct(ratio)}
        label="Query to Active Ratio"
        hint={hint}
      />
    </KpiGrid>
  );
};

/**
 * "Query to Active Ratio Current Month" as a consistent funnel for the newest
 * month in range: Query -> Contacted -> Active, each with its count and share of
 * Query, plus the derived Contactability -> Active ratio.
 *
 * All three figures come from the SAME profit source (ever-contacted /
 * ever-reached-active on the month's created-lead cohort), so the funnel
 * reconciles and matches the profit sheet. (The older version read
 * current-standing active off the lead-status table, which could not carry a
 * contacted figure.) The Overview and the full-screen workbook follow the
 * client's sheet block for block, and the Excel export is read off the table.
 */
export const ActiveRatioTable = ({
  month,
  loading,
}: {
  month: MarketingProfitMonth | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const monthLabel = month?.label;
  const query = month?.overall.query ?? 0;
  const contacted = month?.contactedCount ?? 0;
  const active = month?.activeCount ?? 0;
  const warm = month?.warmCount ?? 0;
  const queryToActive = month?.activeRatio ?? (query > 0 ? (active / query) * 100 : null);
  const queryToWarm = month?.warmRatio ?? (query > 0 ? (warm / query) * 100 : null);
  const queryToContacted =
    month?.contactabilityRatio ?? (query > 0 ? (contacted / query) * 100 : null);
  const contactToActive = contacted > 0 ? (active / contacted) * 100 : null;

  return (
    <ReportSection title="Query to Active Ratio Current Month">
      {loading || !month ? (
        <div className="space-y-2 py-2">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="bg-muted h-8 animate-pulse rounded" />
          ))}
        </div>
      ) : (
        <table className={cn('w-full max-w-md text-[15px]', GRID_TABLE)}>
          <thead>
            <tr>
              <th className={cn(REPORT_HEAD_CELL, 'text-left')}>
                Query to Active Ratio{monthLabel ? ` · ${monthLabel}` : ''}
              </th>
              <th className={cn(REPORT_HEAD_CELL, 'text-foreground text-right text-sm')}>
                {pct(queryToActive)}
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Total Query This Month', value: query, pct: query > 0 ? 100 : null },
              { label: 'Total Contacted This Month', value: contacted, pct: queryToContacted },
              { label: 'Total Active This Month', value: active, pct: queryToActive },
              { label: 'Total Warm This Month', value: warm, pct: queryToWarm },
              { label: 'Contactability to Active Ratio', value: active, pct: contactToActive },
            ].map((row) => (
              <tr key={row.label}>
                <th
                  scope="row"
                  className="text-foreground px-3 py-1.5 text-left font-normal whitespace-nowrap"
                >
                  {row.label}
                </th>
                <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap tabular-nums">
                  {countPct(row.value, row.pct)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportSection>
  );
};

/** Outcome tint so WON / LOST stand out from the open pipeline. */
const bucketClass = (bucket: string | null): string => {
  if (bucket === 'WON') return NUMBER_TONE.good;
  if (bucket === 'LOST') return NUMBER_TONE.bad;
  return '';
};

/** One "N (x%)" cell. */
const StatusCells = ({
  months,
  muted,
  tone,
  headline,
}: {
  months: MarketingStatusMonth[];
  muted?: boolean;
  tone?: string;
  headline?: boolean;
}): React.JSX.Element => (
  <>
    {months.map((month) => (
      <td key={month.month} className={valueCell({ headline, muted, tone })}>
        {count(month.count)}
        <span className="text-muted-foreground ml-1.5 text-xs">{pct(month.pct)}</span>
      </td>
    ))}
  </>
);

/**
 * "Lead Status" - the funnel standing of the queries marketing generated in
 * each month, counted against their CURRENT status.
 *
 * Cohort is the lead's CREATION month, matching Query everywhere else in this
 * report, so a May lead that is now Won still counts under May. Sub-statuses
 * nest behind an expander and are a slice of their parent, not an addition.
 */
export const LeadStatusTable = ({
  status,
  loading,
  showSummary = true,
}: {
  status: MarketingLeadStatusResponse | undefined;
  loading: boolean;
  /**
   * The Query to Active Ratio tiles above the table. Off on the Overview,
   * which carries its own headline tiles at the top of the page - a second set
   * halfway down reads as the page starting over.
   */
  showSummary?: boolean;
}): React.JSX.Element => {
  const groupOpen = useGroupOpen();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const months = status?.months ?? [];

  return (
    <div className="space-y-4">
      {showSummary && <ActiveRatioSummary status={status} loading={loading} />}
      <ReportSection title="Lead Status">
        {loading || !status ? (
          <div className="space-y-2 py-2">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="bg-muted h-8 animate-pulse rounded" />
            ))}
          </div>
        ) : status.statuses.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            No leads in the selected range.
          </p>
        ) : (
          <>
            <table className={MONTH_TABLE}>
              <thead>
                <MonthHeaderRow label="Status" months={months} />
              </thead>
              <tbody>
                {status.statuses.map((entry) => {
                  const open = groupOpen(expanded.has(entry.key));
                  // WON / LOST are where the month actually landed, so they get
                  // the headline rail; the open pipeline stages read as context.
                  const outcome = entry.bucket === 'WON' || entry.bucket === 'LOST';
                  return (
                    <React.Fragment key={entry.key}>
                      <tr className={MONTH_ROW}>
                        <th scope="row" className={labelCell({ headline: outcome })}>
                          {entry.children.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => toggle(entry.key)}
                              title={open ? 'Hide sub-statuses' : 'Show sub-statuses'}
                              className="flex items-center gap-1.5"
                            >
                              <ChevronRight
                                className={cn(
                                  'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                                  open && 'rotate-90',
                                )}
                              />
                              <span className={bucketClass(entry.bucket)}>{entry.name}</span>
                              <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                                {entry.children.length}
                              </span>
                            </button>
                          ) : (
                            <span className={bucketClass(entry.bucket)}>{entry.name}</span>
                          )}
                        </th>
                        <StatusCells
                          months={entry.months}
                          headline={outcome}
                          tone={bucketClass(entry.bucket)}
                        />
                      </tr>
                      {open &&
                        entry.children.map((child) => (
                          <tr key={child.key} className={MONTH_ROW}>
                            <th scope="row" className={labelCell({ depth: 1, muted: true })}>
                              <span className="flex items-center gap-1.5">
                                <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
                                {child.name}
                              </span>
                            </th>
                            <StatusCells months={child.months} muted />
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
                <tr>
                  <th scope="row" className={TOTAL_LABEL_CELL}>
                    Total Queries
                  </th>
                  {status.totals.map((total) => (
                    <td key={total.month} className={TOTAL_VALUE_CELL}>
                      {count(total.count)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>

            <p className="text-muted-foreground mt-3 text-xs">
              Leads are counted by the month they were created and shown against their status today,
              so a May lead that has since been won still counts under May. Sub-status figures are a
              slice of their parent status, not an addition to it.
            </p>
          </>
        )}
      </ReportSection>
    </div>
  );
};
